/**
 * Lust Witch Page
 * 
 * Full deck management interface for personalized arousal guidance.
 * Features:
 * - Draw action cards with tarot flip animation
 * - Manage deck (Actions, Habits, Goals)
 * - 5-option card picker with weighted selection
 * - Cooldown tracking and streak system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Wand2, Target, RefreshCw, Flame, Plus, Trash2,
    Check, X, Clock, ChevronDown, ChevronUp, Sparkles,
    ArrowLeft, Loader2, Lock, Unlock, RotateCcw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';
import { API_BASE } from '../utils/api';

// Card type configuration
const CARD_TYPES = {
    action: {
        label: 'Actions',
        icon: Target,
        color: 'wine',
        timeline: '5 min–1 hr',
        description: 'Quick techniques to do right now',
        addCooldown: '1 per day'
    },
    habit: {
        label: 'Habits',
        icon: RefreshCw,
        color: 'purple',
        timeline: '2×/day–Weekly',
        description: 'Regular practices to build',
        addCooldown: '1 per week'
    },
    goal: {
        label: 'Goals',
        icon: Flame,
        color: 'amber',
        timeline: '2–8 weeks',
        description: 'Long-term objectives',
        addCooldown: 'Unlimited'
    }
};

// Tarot card flip animation variants
const cardVariants = {
    hidden: { rotateY: 180, opacity: 0, scale: 0.8 },
    visible: {
        rotateY: 0,
        opacity: 1,
        scale: 1,
        transition: { duration: 0.6, type: 'spring', stiffness: 100 }
    },
    exit: {
        rotateY: -180,
        opacity: 0,
        scale: 0.8,
        transition: { duration: 0.4 }
    }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function TypeTabs({ activeType, onTypeChange, deckCounts }) {
    return (
        <div className="flex gap-2 mb-6">
            {Object.entries(CARD_TYPES).map(([type, config]) => {
                const Icon = config.icon;
                const isActive = activeType === type;
                const count = deckCounts[type] || 0;

                return (
                    <button
                        key={type}
                        onClick={() => onTypeChange(type)}
                        className={`
                            flex-1 flex items-center justify-center gap-2 py-3 px-4 
                            border transition-all duration-200
                            ${isActive
                                ? `bg-${config.color}-600/20 border-${config.color}-500 text-white`
                                : 'bg-dark-800/50 border-dark-700 text-cream/60 hover:border-dark-600'
                            }
                        `}
                    >
                        <Icon size={18} />
                        <span className="font-medium">{config.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 ${isActive ? 'bg-white/20' : 'bg-dark-700'}`}>
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function DrawArea({ drawState, onDraw, onComplete, loading }) {
    const currentCard = drawState?.currentCard;
    const canDraw = drawState?.canDraw;
    const nextDrawTime = drawState?.state?.next_draw_utc;
    const streak = drawState?.state?.streak_count || 0;
    const cooldownHours = drawState?.state?.cooldown_hours || 6;

    // Format time remaining
    const formatTimeRemaining = (isoDate) => {
        if (!isoDate) return null;
        const remaining = new Date(isoDate) - new Date();
        if (remaining <= 0) return null;

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const timeRemaining = formatTimeRemaining(nextDrawTime);

    return (
        <div className="glass-card border border-purple-500/30 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold text-cream flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-400" />
                    Draw an Action
                </h2>
                {streak > 0 && (
                    <div className="flex items-center gap-1 px-3 py-1 bg-amber-900/30 text-amber-400 text-sm">
                        <Flame size={14} />
                        <span>{streak} streak</span>
                    </div>
                )}
            </div>

            <AnimatePresence mode="wait">
                {currentCard ? (
                    <motion.div
                        key="card"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="bg-gradient-to-br from-purple-900/50 to-wine-900/50 border border-purple-400/30 p-6 mb-4"
                        style={{ perspective: 1000, transformStyle: 'preserve-3d' }}
                    >
                        <div className="text-xs text-purple-400 uppercase tracking-wider mb-2">
                            Current Task
                        </div>
                        <h3 className="text-xl font-bold text-cream mb-3">
                            {currentCard.arousal_mechanics?.name || 'Unknown'}
                        </h3>
                        <p className="text-cream/70 mb-4">
                            {currentCard.arousal_mechanics?.summary}
                        </p>
                        <div className="text-xs text-dark-400 mb-4">
                            Category: {currentCard.arousal_mechanics?.category}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => onComplete('complete')}
                                disabled={loading}
                                className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-colors disabled:opacity-50"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                                Complete
                            </button>
                            <button
                                onClick={() => onComplete('reroll')}
                                disabled={loading}
                                className="px-4 py-3 bg-dark-700 hover:bg-dark-600 text-cream/70 transition-colors disabled:opacity-50"
                                title="Reroll (+2hr penalty)"
                            >
                                <RotateCcw size={18} />
                            </button>
                        </div>
                    </motion.div>
                ) : canDraw ? (
                    <motion.button
                        key="draw"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        onClick={onDraw}
                        disabled={loading}
                        className="w-full py-6 bg-gradient-to-r from-purple-600 to-wine-600 hover:from-purple-500 hover:to-wine-500 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin" size={24} />
                        ) : (
                            <>
                                <Wand2 size={24} />
                                Draw Action Card
                            </>
                        )}
                    </motion.button>
                ) : (
                    <motion.div
                        key="cooldown"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-8 bg-dark-800/50"
                    >
                        <Clock className="w-10 h-10 mx-auto mb-3 text-purple-400/50" />
                        <p className="text-cream/50 mb-1">Next draw available in</p>
                        <p className="text-2xl font-bold text-purple-400">{timeRemaining || 'Soon'}</p>
                        <p className="text-xs text-dark-500 mt-2">
                            Base cooldown: {cooldownHours}h
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function DeckCard({ card, onRemove, removing }) {
    const mechanic = card.arousal_mechanics;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="bg-dark-800/50 border border-dark-700 p-4 hover:border-dark-600 transition-colors"
        >
            <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                    <Link
                        to={`/arousal/${mechanic?.slug || card.arousal_mechanic_id}`}
                        className="font-bold text-cream hover:text-wine-300 transition-colors"
                    >
                        {mechanic?.name || 'Loading...'}
                    </Link>
                    <p className="text-sm text-dark-400 line-clamp-3 mt-1">
                        {mechanic?.summary}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-dark-500">
                        <span>{mechanic?.category}</span>
                        <span>•</span>
                        <span>Added {new Date(card.added_at).toLocaleDateString()}</span>
                    </div>
                </div>
                <button
                    onClick={() => onRemove(card.id)}
                    disabled={removing}
                    className="p-2 text-dark-500 hover:text-red-400 transition-colors disabled:opacity-50"
                    title="Remove from deck"
                >
                    {removing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </button>
            </div>
        </motion.div>
    );
}

function AddCardModal({ cardType, options, loading, onAdd, onReroll, onClose, canAdd, nextAddAt }) {
    const config = CARD_TYPES[cardType];

    const formatTimeRemaining = (isoDate) => {
        if (!isoDate) return null;
        const remaining = new Date(isoDate) - new Date();
        if (remaining <= 0) return null;

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-dark-900 border border-dark-700 max-w-2xl w-full max-h-[80vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b border-dark-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-${config.color}-900/50 flex items-center justify-center`}>
                            <config.icon className={`w-5 h-5 text-${config.color}-400`} />
                        </div>
                        <div>
                            <h3 className="font-bold text-cream">Add {config.label}</h3>
                            <p className="text-xs text-dark-400">{config.addCooldown}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-dark-400 hover:text-cream">
                        <X size={20} />
                    </button>
                </div>

                {/* Not allowed to add */}
                {!canAdd && (
                    <div className="p-8 text-center">
                        <Lock className="w-12 h-12 mx-auto mb-4 text-dark-500" />
                        <p className="text-cream/70 mb-2">Cooldown Active</p>
                        <p className="text-lg font-bold text-wine-400">
                            {formatTimeRemaining(nextAddAt)}
                        </p>
                    </div>
                )}

                {/* Options */}
                {canAdd && (
                    <>
                        <div className="p-4 max-h-[50vh] overflow-y-auto space-y-3">
                            {loading ? (
                                <div className="py-8 flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 animate-spin text-wine-400" />
                                </div>
                            ) : options.length === 0 ? (
                                <div className="py-8 text-center text-dark-400">
                                    No more options available
                                </div>
                            ) : (
                                options.map((option) => (
                                    <motion.button
                                        key={option.id}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => onAdd(option.id)}
                                        className="w-full text-left p-4 bg-dark-800/50 border border-dark-700 hover:border-wine-500/50 transition-all"
                                    >
                                        <div className="font-bold text-cream mb-1">{option.name}</div>
                                        <p className="text-sm text-dark-400 line-clamp-3">{option.summary}</p>
                                        <div className="flex items-center gap-2 mt-2 text-xs text-dark-500">
                                            <span>{option.category}</span>
                                            <span>•</span>
                                            <span>Effort: {option.effort_level || 'Medium'}</span>
                                        </div>
                                    </motion.button>
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-dark-700 flex items-center justify-between">
                            <button
                                onClick={onReroll}
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 text-dark-400 hover:text-cream transition-colors disabled:opacity-50"
                            >
                                <RefreshCw size={16} />
                                Reroll Options
                            </button>
                            <div className="text-xs text-dark-500">
                                Pick one to add to your deck
                            </div>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
}

// ============================================================================
// HABIT TRACKING SECTION
// ============================================================================

function HabitSection({ personaId, getAuthHeaders, deck }) {
    const [habits, setHabits] = useState([]);
    const [workQueue, setWorkQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(null);

    const fetchHabits = useCallback(async () => {
        if (!personaId) return;

        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/habits/${personaId}/today`, { headers });

            if (res.ok) {
                const data = await res.json();
                setHabits(data.habits || []);
                setWorkQueue(data.workQueue || []);
            }
        } catch (error) {
            console.error('Error fetching habits:', error);
        } finally {
            setLoading(false);
        }
    }, [personaId, getAuthHeaders]);

    useEffect(() => {
        fetchHabits();
    }, [fetchHabits]);

    const handleConfirm = async (deckCardId, confirmed) => {
        setConfirming(deckCardId);
        try {
            const headers = await getAuthHeaders();
            await fetch(`${API_BASE}/api/lust-witch/habits/${personaId}/confirm`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ deckCardId, confirmed })
            });
            await fetchHabits();
        } catch (error) {
            console.error('Error confirming habit:', error);
        } finally {
            setConfirming(null);
        }
    };

    if (loading) {
        return (
            <div className="glass-card border border-purple-500/30 p-6 mb-6">
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                </div>
            </div>
        );
    }

    const pendingHabits = habits.filter(h => h.confirmed === null);
    const completedToday = habits.filter(h => h.confirmed === true).length;
    const skippedToday = habits.filter(h => h.confirmed === false).length;

    return (
        <div className="glass-card border border-purple-500/30 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold text-cream flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-purple-400" />
                    Today's Habits
                </h2>
                <div className="flex items-center gap-3 text-sm">
                    <span className="text-emerald-400">✓ {completedToday}</span>
                    <span className="text-dark-500">/ {habits.length}</span>
                </div>
            </div>

            {habits.length === 0 ? (
                <div className="text-center py-8 text-dark-400">
                    <RefreshCw className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No habits in your deck yet</p>
                    <p className="text-sm mt-1">Add habits to start tracking</p>
                </div>
            ) : (
                <>
                    {/* Pending habits */}
                    {pendingHabits.length > 0 && (
                        <div className="space-y-3 mb-4">
                            <div className="text-xs text-dark-400 uppercase tracking-wider mb-2">
                                Awaiting Confirmation
                            </div>
                            {pendingHabits.map(habit => (
                                <motion.div
                                    key={habit.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-dark-800/50 border border-dark-700 p-4"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-cream">
                                                {habit.arousal_mechanics?.name}
                                            </div>
                                            <p className="text-sm text-dark-400 line-clamp-1 mt-1">
                                                {habit.arousal_mechanics?.summary}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            <button
                                                onClick={() => handleConfirm(habit.id, true)}
                                                disabled={confirming === habit.id}
                                                className="w-10 h-10 flex items-center justify-center bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 border border-emerald-500/30 transition-colors disabled:opacity-50"
                                                title="Did it"
                                            >
                                                {confirming === habit.id ? (
                                                    <Loader2 size={16} className="animate-spin" />
                                                ) : (
                                                    <Check size={18} />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleConfirm(habit.id, false)}
                                                disabled={confirming === habit.id}
                                                className="w-10 h-10 flex items-center justify-center bg-dark-700/50 hover:bg-dark-600 text-dark-400 border border-dark-600 transition-colors disabled:opacity-50"
                                                title="Skipped"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Completed habits summary */}
                    {(completedToday > 0 || skippedToday > 0) && pendingHabits.length === 0 && (
                        <div className="text-center py-6 bg-emerald-900/10 border border-emerald-500/20">
                            <Check className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                            <p className="text-cream">All habits logged for today!</p>
                            <p className="text-sm text-dark-400 mt-1">
                                {completedToday} completed • {skippedToday} skipped
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* Work Queue */}
            {workQueue.length > 0 && (
                <div className="mt-6 pt-4 border-t border-dark-700">
                    <div className="text-xs text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Flame size={12} />
                        Work Queue ({workQueue.length})
                    </div>
                    <p className="text-xs text-dark-400 mb-3">
                        These habits need 7 consecutive days to exit the queue
                    </p>
                    <div className="space-y-2">
                        {workQueue.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center justify-between bg-amber-900/10 border border-amber-500/20 p-3"
                            >
                                <span className="text-sm text-cream">
                                    {item.deck_card?.arousal_mechanics?.name}
                                </span>
                                <span className="text-xs text-amber-400">
                                    {item.work_queue_streak}/7 days
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function LustWitchPage() {
    const { activePersona, getAuthHeaders } = useAuth();

    // State
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [activeType, setActiveType] = useState('action');
    const [deck, setDeck] = useState([]);
    const [deckCounts, setDeckCounts] = useState({ action: 0, habit: 0, goal: 0 });
    const [drawState, setDrawState] = useState(null);
    const [cooldowns, setCooldowns] = useState(null);

    // Modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addOptions, setAddOptions] = useState([]);
    const [addLoading, setAddLoading] = useState(false);

    const [removingCard, setRemovingCard] = useState(null);

    // Fetch all data
    const fetchData = useCallback(async () => {
        if (!activePersona) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const headers = await getAuthHeaders();

            // Fetch deck
            const deckRes = await fetch(`${API_BASE}/api/lust-witch/deck/${activePersona.id}`, { headers });
            if (deckRes.ok) {
                const data = await deckRes.json();
                setDeck(data.deck || []);
                setDeckCounts(data.counts || { action: 0, habit: 0, goal: 0 });
            }

            // Fetch draw state
            const drawRes = await fetch(`${API_BASE}/api/lust-witch/draw/${activePersona.id}`, { headers });
            if (drawRes.ok) {
                const data = await drawRes.json();
                setDrawState(data);
            }

            // Fetch cooldowns
            const cdRes = await fetch(`${API_BASE}/api/lust-witch/cooldowns/${activePersona.id}`, { headers });
            if (cdRes.ok) {
                const data = await cdRes.json();
                setCooldowns(data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [activePersona, getAuthHeaders]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Draw card
    const handleDraw = async () => {
        if (!activePersona || actionLoading) return;

        setActionLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/draw/${activePersona.id}`, {
                method: 'POST',
                headers
            });

            if (res.ok) {
                const data = await res.json();
                setDrawState(prev => ({ ...prev, ...data, canDraw: false }));
            }
        } catch (error) {
            console.error('Error drawing card:', error);
        } finally {
            setActionLoading(false);
        }
    };

    // Complete/Reroll card
    const handleComplete = async (action) => {
        if (!activePersona || actionLoading) return;

        setActionLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/complete/${activePersona.id}`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action })
            });

            if (res.ok) {
                await fetchData();
            }
        } catch (error) {
            console.error('Error completing card:', error);
        } finally {
            setActionLoading(false);
        }
    };

    // Open add modal and fetch options
    const openAddModal = async (type) => {
        setActiveType(type);
        setShowAddModal(true);
        await fetchOptions(type);
    };

    // Fetch card options
    const fetchOptions = async (type) => {
        if (!activePersona) return;

        setAddLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/options/${activePersona.id}/${type}`, { headers });

            if (res.ok) {
                const data = await res.json();
                setAddOptions(data.options || []);
                setCooldowns(prev => ({
                    ...prev,
                    [type]: { canAdd: data.canAdd, nextAddAt: data.nextAddAt }
                }));
            }
        } catch (error) {
            console.error('Error fetching options:', error);
        } finally {
            setAddLoading(false);
        }
    };

    // Add card to deck
    const handleAddCard = async (mechanicId) => {
        if (!activePersona || addLoading) return;

        setAddLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/deck`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    personaId: activePersona.id,
                    arousalMechanicId: mechanicId,
                    cardType: activeType
                })
            });

            if (res.ok) {
                setShowAddModal(false);
                await fetchData();
            }
        } catch (error) {
            console.error('Error adding card:', error);
        } finally {
            setAddLoading(false);
        }
    };

    // Remove card from deck
    const handleRemoveCard = async (cardId) => {
        if (!activePersona) return;

        setRemovingCard(cardId);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/deck/${activePersona.id}/${cardId}`, {
                method: 'DELETE',
                headers
            });

            if (res.ok) {
                await fetchData();
            }
        } catch (error) {
            console.error('Error removing card:', error);
        } finally {
            setRemovingCard(null);
        }
    };

    // Filter deck by active type
    const filteredDeck = deck.filter(c => c.card_type === activeType);

    // Not logged in
    if (!activePersona) {
        return (
            <div className="min-h-screen bg-charcoal-900 flex items-center justify-center">
                <div className="text-center">
                    <Wand2 className="w-16 h-16 mx-auto mb-4 text-purple-400/50" />
                    <h2 className="text-xl font-bold text-cream mb-2">Sign in to access the Lust Witch</h2>
                    <Link to="/login" className="text-wine-400 hover:text-wine-300">
                        Sign in →
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal-900 flex items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-charcoal-900">
            {/* Header */}
            <div className="bg-gradient-to-b from-purple-900/20 to-transparent border-b border-purple-500/20">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <Link
                        to="/profile"
                        className="inline-flex items-center gap-2 text-dark-400 hover:text-cream text-sm mb-4"
                    >
                        <ArrowLeft size={16} />
                        Back to Profile
                    </Link>

                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-purple-900/50 flex items-center justify-center">
                            <Wand2 className="w-8 h-8 text-purple-400" />
                        </div>
                        <div>
                            <h1 className="font-display text-3xl font-bold text-cream">Lust Witch</h1>
                            <p className="text-dark-400">Your personal arousal guidance deck</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                {/* Draw Area */}
                {deckCounts.action > 0 && (
                    <DrawArea
                        drawState={drawState}
                        onDraw={handleDraw}
                        onComplete={handleComplete}
                        loading={actionLoading}
                    />
                )}

                {/* Habit Tracking Section */}
                {deckCounts.habit > 0 && (
                    <HabitSection
                        personaId={activePersona.id}
                        getAuthHeaders={getAuthHeaders}
                        deck={deck}
                    />
                )}

                {/* Type Tabs */}
                <TypeTabs
                    activeType={activeType}
                    onTypeChange={setActiveType}
                    deckCounts={deckCounts}
                />

                {/* Type Info */}
                <div className="mb-6 p-4 bg-dark-800/50 border border-dark-700">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-cream">{CARD_TYPES[activeType].label}</h3>
                            <p className="text-sm text-dark-400">{CARD_TYPES[activeType].description}</p>
                        </div>
                        <button
                            onClick={() => openAddModal(activeType)}
                            className="flex items-center gap-2 px-4 py-2 bg-wine-600 hover:bg-wine-500 text-white transition-colors"
                        >
                            <Plus size={16} />
                            Add Card
                        </button>
                    </div>
                </div>

                {/* Deck Cards */}
                <div className="space-y-3">
                    <AnimatePresence>
                        {filteredDeck.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-12 bg-dark-800/30 border border-dark-700"
                            >
                                <Sparkles className="w-12 h-12 mx-auto mb-4 text-dark-500" />
                                <p className="text-cream/50 mb-4">No {CARD_TYPES[activeType].label.toLowerCase()} in your deck yet</p>
                                <button
                                    onClick={() => openAddModal(activeType)}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-wine-600 hover:bg-wine-500 text-white transition-colors"
                                >
                                    <Plus size={16} />
                                    Add Your First Card
                                </button>
                            </motion.div>
                        ) : (
                            filteredDeck.map(card => (
                                <DeckCard
                                    key={card.id}
                                    card={card}
                                    onRemove={handleRemoveCard}
                                    removing={removingCard === card.id}
                                />
                            ))
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Add Card Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <AddCardModal
                        cardType={activeType}
                        options={addOptions}
                        loading={addLoading}
                        onAdd={handleAddCard}
                        onReroll={() => fetchOptions(activeType)}
                        onClose={() => setShowAddModal(false)}
                        canAdd={cooldowns?.[activeType]?.canAdd !== false}
                        nextAddAt={cooldowns?.[activeType]?.nextAddAt}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
